import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../../context/AuthContext'
import { AdminAlert } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import { adminGetSeasons, adminCreateSeason, adminUpdateSeason, adminDeleteSeason, adminActivateSeason, adminEndSeason } from '../../../services/admin'
import { SeasonDetail } from './SeasonDetail'

const STATUS_STYLE = {
  upcoming: 'border-on-surface-variant/40 text-on-surface-variant',
  active:   'border-primary-container bg-primary-container/20 text-on-primary-container',
  ended:    'border-on-background/30 bg-surface-container text-on-surface-variant',
}

function toDateInput(d) {
  if (!d) return ''
  const date = new Date(d)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

function farFutureEndDate(startDate) {
  const base = startDate ? new Date(startDate) : new Date()
  base.setFullYear(base.getFullYear() + 10)
  return base.toISOString().slice(0, 10)
}

function SeasonModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial
  const [seasonName, setSeasonName] = useState(initial?.seasonName ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [startDate, setStartDate] = useState(toDateInput(initial?.startDate))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const submit = async () => {
    if (!seasonName.trim()) { setError('Season name is required'); return }
    if (!startDate) { setError('Start date is required'); return }
    setLoading(true)
    setError(null)
    try {
      const endDate = isEdit ? toDateInput(initial?.endDate) || farFutureEndDate(startDate) : farFutureEndDate(startDate)
      const payload = { seasonName, description, startDate, endDate }
      if (isEdit) await adminUpdateSeason(initial.id, payload)
      else await adminCreateSeason(payload)
      onSaved()
    } catch (err) {
      setError(
        isEdit
          ? 'Update failed — this backend endpoint currently errors on every season (known backend bug, flagged to Joshua).'
          : err.message ?? 'Failed to create season'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-surface border-4 border-on-background w-full max-w-md flex flex-col gap-5 p-6"
        style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
        <div className="flex items-center justify-between">
          <h2 className="font-extrabold text-[14px] uppercase tracking-widest">{isEdit ? 'Edit Season' : 'New Season'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-surface-container transition-colors">
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>
        {error && <AdminAlert type="error" message={error} />}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Season Name</label>
            <input type="text" placeholder="e.g. Spring 2026" value={seasonName} onChange={e => setSeasonName(e.target.value)}
              className="bg-surface border-2 border-on-background px-3 py-2 text-[14px] focus:outline-none focus:border-primary-container" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Description</label>
            <textarea rows={2} placeholder="Optional" value={description} onChange={e => setDescription(e.target.value)}
              className="bg-surface border-2 border-on-background px-3 py-2 text-[13px] focus:outline-none focus:border-primary-container resize-none" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="bg-surface border-2 border-on-background px-3 py-2 text-[13px] font-mono focus:outline-none focus:border-primary-container" />
            <p className="text-[10px] text-on-surface-variant">No end date — the season runs until an admin ends it manually.</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors">
            Cancel
          </button>
          <button onClick={submit} disabled={loading}
            className="flex-1 py-2.5 bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50">
            {loading ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Season'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function SeasonRewards() {
  const { hasPermission } = useAuth()
  const canEdit = hasPermission('seasons')
  const [seasons, setSeasons] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [modal, setModal] = useState(null) // null | 'new' | seasonObj
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [endTarget, setEndTarget] = useState(null)
  const [ending, setEnding] = useState(false)
  const [activateTarget, setActivateTarget] = useState(null)
  const [activating, setActivating] = useState(false)
  const [successMsg, setSuccessMsg] = useState(null)
  const [openSeasonId, setOpenSeasonId] = useState(null) // clicking a season opens its detail view

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await adminGetSeasons()
      setSeasons(data)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleSaved = () => {
    setModal(null)
    setSuccessMsg(modal === 'new' ? 'Season created' : 'Season updated')
    setTimeout(() => setSuccessMsg(null), 4000)
    load()
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await adminDeleteSeason(deleteTarget.id)
      setDeleteTarget(null)
      setSuccessMsg('Season deleted')
      setTimeout(() => setSuccessMsg(null), 4000)
      load()
    } catch (err) {
      setError(err)
      setDeleteTarget(null)
    } finally {
      setDeleting(false)
    }
  }

  const confirmActivate = async () => {
    if (!activateTarget) return
    setActivating(true)
    try {
      await adminActivateSeason(activateTarget.id)
      setActivateTarget(null)
      setSuccessMsg(`"${activateTarget.seasonName}" is now active — users can convert CIS to CPT.`)
      setTimeout(() => setSuccessMsg(null), 5000)
      load()
    } catch (err) {
      setError(err)
      setActivateTarget(null)
    } finally {
      setActivating(false)
    }
  }

  const confirmEnd = async () => {
    if (!endTarget) return
    setEnding(true)
    try {
      await adminEndSeason(endTarget.id)
      setEndTarget(null)
      setSuccessMsg(`"${endTarget.seasonName}" ended. Conversion setup and payout are now available.`)
      setTimeout(() => setSuccessMsg(null), 5000)
      load()
    } catch (err) {
      setError(err)
      setEndTarget(null)
    } finally {
      setEnding(false)
    }
  }

  return (
    <>
      {modal && <SeasonModal initial={modal === 'new' ? null : modal} onClose={() => setModal(null)} onSaved={handleSaved} />}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4" onClick={e => { if (e.target === e.currentTarget) setDeleteTarget(null) }}>
          <div className="bg-surface border-4 border-on-background w-full max-w-sm flex flex-col gap-5 p-6"
            style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
            <h2 className="font-extrabold text-[14px] uppercase tracking-widest">Delete Season?</h2>
            <p className="text-[13px] text-on-surface-variant">
              This will permanently delete <span className="font-bold text-on-surface">{deleteTarget.seasonName}</span>. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors">
                Cancel
              </button>
              <button onClick={confirmDelete} disabled={deleting}
                className="flex-1 py-2.5 bg-error text-on-error font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50">
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activateTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4" onClick={e => { if (e.target === e.currentTarget) setActivateTarget(null) }}>
          <div className="bg-surface border-4 border-on-background w-full max-w-sm flex flex-col gap-5 p-6"
            style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
            <h2 className="font-extrabold text-[14px] uppercase tracking-widest">Activate Season?</h2>
            <p className="text-[13px] text-on-surface-variant">
              This will activate <span className="font-bold text-on-surface">{activateTarget.seasonName}</span> — users will
              immediately be able to convert CIS into CPT for this season. Requires today's date to be on or after the season's
              start date.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setActivateTarget(null)} className="flex-1 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors">
                Cancel
              </button>
              <button onClick={confirmActivate} disabled={activating}
                className="flex-1 py-2.5 bg-primary-container text-on-primary-fixed font-bold text-[11px] uppercase tracking-widest hover:brightness-105 transition-all disabled:opacity-50">
                {activating ? 'Activating…' : 'Activate Season'}
              </button>
            </div>
          </div>
        </div>
      )}

      {endTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4" onClick={e => { if (e.target === e.currentTarget) setEndTarget(null) }}>
          <div className="bg-surface border-4 border-on-background w-full max-w-sm flex flex-col gap-5 p-6"
            style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
            <h2 className="font-extrabold text-[14px] uppercase tracking-widest">End Season?</h2>
            <p className="text-[13px] text-on-surface-variant">
              This will end <span className="font-bold text-on-surface">{endTarget.seasonName}</span> right now. Once ended, the conversion
              rate simulator and payout tools unlock for this season — this cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setEndTarget(null)} className="flex-1 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors">
                Cancel
              </button>
              <button onClick={confirmEnd} disabled={ending}
                className="flex-1 py-2.5 bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50">
                {ending ? 'Ending…' : 'End Season'}
              </button>
            </div>
          </div>
        </div>
      )}

      {openSeasonId ? (
        <SeasonDetail
          season={seasons.find(s => s.id === openSeasonId)}
          onBack={() => { setOpenSeasonId(null); load() }}
          onEndSeason={() => setEndTarget(seasons.find(s => s.id === openSeasonId))}
          onActivateSeason={() => setActivateTarget(seasons.find(s => s.id === openSeasonId))}
        />
      ) : (
      <div className="flex flex-col gap-6">
        {successMsg && <AdminAlert type="success" message={successMsg} />}
        {error && <AdminAlert type="error" message={error.message ?? 'Failed to load seasons'} />}

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[12px] text-on-surface-variant">
            Create and manage reward seasons. Click a season to open its conversion &amp; payout tools — those unlock once the season is ended.
          </p>
          {canEdit && <button onClick={() => setModal('new')}
            className="px-5 py-2.5 border-4 border-on-background bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity shrink-0"
            style={{ boxShadow: '3px 3px 0px 0px var(--neo-border-color)' }}>
            + New Season
          </button>}
        </div>

        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded" />)}
          </div>
        ) : seasons.length === 0 ? (
          <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-10 text-center">
            <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">No seasons yet — create one to get started</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {seasons.map(s => (
              <div key={s.id}
                onClick={() => setOpenSeasonId(s.id)}
                role="button" tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter') setOpenSeasonId(s.id) }}
                className="border-4 border-on-background bg-surface rounded-lg p-4 flex items-start justify-between gap-4 flex-wrap cursor-pointer hover:bg-surface-container-low transition-colors"
                style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
                <div className="flex flex-col gap-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-[15px] leading-tight">{s.seasonName}</h3>
                    <span className={`px-2 py-0.5 border-2 text-[10px] font-bold uppercase tracking-widest rounded ${STATUS_STYLE[s.status] ?? STATUS_STYLE.upcoming}`}>
                      {s.status ?? 'upcoming'}
                    </span>
                  </div>
                  {s.description && <p className="text-[12px] text-on-surface-variant">{s.description}</p>}
                  <p className="text-[12px] text-on-surface-variant font-mono">
                    Started {toDateInput(s.startDate) || '—'}
                  </p>
                </div>
                {canEdit && <div className="flex gap-2 shrink-0 flex-wrap" onClick={e => e.stopPropagation()}>
                  {s.status === 'upcoming' && (
                    <button onClick={() => setActivateTarget(s)}
                      className="px-3 py-1.5 bg-primary-container text-on-primary-fixed font-bold text-[10px] uppercase tracking-widest hover:brightness-105 transition-all rounded">
                      Activate
                    </button>
                  )}
                  {s.status !== 'ended' && (
                    <button onClick={() => setEndTarget(s)}
                      className="px-3 py-1.5 border-2 border-primary-container text-on-primary-container font-bold text-[10px] uppercase tracking-widest hover:bg-primary-container hover:text-on-primary-fixed transition-colors rounded">
                      End Season
                    </button>
                  )}
                  <button onClick={() => setModal(s)}
                    className="px-3 py-1.5 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-primary-container hover:text-on-primary-container transition-colors rounded">
                    Edit
                  </button>
                  <button onClick={() => setDeleteTarget(s)}
                    className="px-3 py-1.5 border-2 border-error text-error font-bold text-[10px] uppercase tracking-widest hover:bg-error hover:text-on-error transition-colors rounded">
                    Delete
                  </button>
                </div>}
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </>
  )
}